import { useForm } from "react-hook-form";
import type { CommunityMemberBillingInfo } from "@/react/types";
import { paywallCurrencyCodes } from "@/react/types/PaywallCurrencyCodes";
import { countryCodes } from "@/react/utils/paywalls/countryCodes";
import type { CheckoutFormValuesProps } from "../CheckoutFormValuesProps";
import type { CheckoutProps } from "../CheckoutProps";
import { getCurrentUserName } from "../helpers/users";
import { useCheckoutPaywallPrice } from "./useCheckoutPaywallPrice";

const getDefaultAddressCountry = (
  isBrazilianCommunity: boolean,
  paywallCurrencyCode: string,
  addressCountry: string | undefined,
): string | undefined => {
  if (addressCountry) {
    return addressCountry;
  }
  if (
    isBrazilianCommunity &&
    paywallCurrencyCode === paywallCurrencyCodes.brl
  ) {
    return countryCodes.br;
  }
  return undefined;
};
const getDefaultCommunityMemberBillingInfoAttributes = (
  isBrazilianCommunity: boolean,
  paywallCurrencyCode: string,
  isMemberAddressComplete: boolean,
  memberBillingInfo?: CommunityMemberBillingInfo,
): CheckoutFormValuesProps["community_member_billing_info_attributes"] => {
  const defaultAddressCountry = getDefaultAddressCountry(
    isBrazilianCommunity,
    paywallCurrencyCode,
    memberBillingInfo?.address_country,
  );
  return {
    address_city: memberBillingInfo?.address_city,
    address_complete: isMemberAddressComplete,
    address_country: defaultAddressCountry,
    address_line1: memberBillingInfo?.address_line1,
    address_line2: memberBillingInfo?.address_line2,
    address_postal_code: memberBillingInfo?.address_postal_code,
    address_state: memberBillingInfo?.address_state,
    business_name: memberBillingInfo?.business_name,
    tax_id_type: memberBillingInfo?.tax_id_type,
    tax_id_value: memberBillingInfo?.tax_id_value,
  };
};

// TODO: Use `CheckoutFormValuesProps` type for `useForm` once we have completed the checkout refactor
// TODO: pre-populate `community_member_billing_info_attributes` with `props.memberBillingInfo` if present
export const useCheckoutFormMethods = (props: CheckoutProps) => {
  const {
    currentCommunity,
    currentUser,
    isMemberAddressComplete,
    isBrazilianAccountAndProcessorStripeBr,
    memberBillingInfo,
    paywall,
  } = props;
  const paywallPrice = useCheckoutPaywallPrice(props);

  return useForm({
    defaultValues: {
      community_id: currentCommunity.id,
      paywall_price_id: paywallPrice.id,
      user_id: currentUser?.id,
      email: currentUser?.email,
      name: getCurrentUserName(currentUser),
      payment_method_type: "card",
      coupon_code_applied: false,
      community_member_billing_info_attributes:
        getDefaultCommunityMemberBillingInfoAttributes(
          isBrazilianAccountAndProcessorStripeBr,
          paywall?.currency?.code,
          isMemberAddressComplete,
          memberBillingInfo,
        ),
      community_member_billing_info_address_attributes_complete:
        isMemberAddressComplete,
    },
  });
};
